import { CSS, FastLaneSection, type MediaType, PixelMap, TickerIconType } from '../types';
import { ABSOLUTE_FILL, convertPixelPitchForTicker, rpx, useTranslatable } from '../helpers';
import Typography from './typography';
import { useFastLane } from '../store';
import React, { useMemo } from 'react';
import TickerIcon from './ticker-icon';
import { usePixelMap } from '../store/fast-lane-context';
import { TICKER_HEIGHT } from '../shared';
import logo from '../../../../live/animations/program-messaging/displays/Logo';

interface TickerSectionContentProps {
  section: FastLaneSection;
  index: number;
}

function calculateHeight(pixels: any, pixelMap: PixelMap) {
  if (!pixels || pixels.length === 0) {
    return null;
  }

  const pxNumber = typeof pixels === 'string' ? parseFloat(pixels) : pixels;

  return pxNumber / TICKER_HEIGHT * pixelMap.contentHeight;
}

export default function TickerSectionContent({ section, index }: TickerSectionContentProps) {
  const pixelMap = usePixelMap();
  const theme = useFastLane((f) => f.getTheme());

  const offsetLeft = useMemo(() => {
    if (section?.position?.tickerOffsetLeft !== undefined) {
      return convertPixelPitchForTicker(section.position.tickerOffsetLeft, pixelMap);
    }

    return 0;
  }, [section, index]);

  const primary = useTranslatable(section.ticker.primary);
  const secondary = useTranslatable(section.ticker.secondary);

  return (
    <div
      style={{
        ...ABSOLUTE_FILL,
        paddingLeft: theme.tickerPaddingLeft,
      }}
      className="ticker-section-content"
    >
      <Slide
        background={'transparent'}
        text={'var(--primary-color, white)'}
        content={!section.position?.showPrimaryTicker ? primary : ''}
        icon={section?.ticker?.primaryIcon}
      />

      {
        theme.includeLogo && section?.logo?.url ? (
          <LogoSlide
            logo={section.logo}
            background={'transparent'}
          />
        ) : (
          <Slide
            background={'transparent'}
            text={theme?.bothTickersBlack ? 'var(--primary-color, white)' : '#000'}
            content={!section.position?.showSecondaryTicker ? secondary : ''}
            icon={section?.ticker?.secondaryIcon}
          />
        )
      }
    </div>
  );
}

function LogoSlide({ logo, background }: { logo: FastLaneSection['logo'], background: string }) {
  const padding = logo?.increase_size ? 0 : rpx(2, 'positioning');
  
  return (
    <div
      style={{
        height: '50%',
        width: '100%',
        // backgroundColor: background,
        // color: text,
        display: 'flex',
        alignItems: 'center',
        // border: '1px solid blue',
        // boxSizing: 'border-box',
        gap: 20,
        position: 'relative',
      }}
    >
      <img
        src={logo?.url}
        style={{
          paddingTop: padding,
          paddingBottom: padding,
          // height: logo?.push_down ? '65%' : '50%',
          height: '75%',
          width: 'auto',
          // width: rpx(200, 'positioning'),
          // filter: isDark ? 'invert(1)' : undefined,
          filter: 'invert(1)',
          // marginLeft: rpx(-8, 'positioning'),
        }}
      />
    </div>
  );
}

function Slide({
                 background,
                 content,
                 text,
                 icon,
               }: {
  background: string;
  content: string;
  text: string;
  offsetLeft?: number;
  icon?: TickerIconType;
}) {
  const pixelMap = usePixelMap();
  const theme = useFastLane((f) => f.getTheme());

  const tickerStyle = useMemo(() => {
    const {
      fontSize,
      lineHeight,
      paddingTop,
      ...other
    } = theme.style.ticker;

    return {
      ...other,
      fontSize: calculateHeight(fontSize, pixelMap),
      lineHeight: calculateHeight(lineHeight, pixelMap),
      paddingTop: calculateHeight(paddingTop, pixelMap),
    };
  }, [theme.style.ticker]) as CSS;

  return (
    <div
      style={{
        height: '50%',
        width: '100%',
        backgroundColor: background,
        color: text,
        display: 'flex',
        alignItems: 'center',
        // border: '1px solid blue',
        // boxSizing: 'border-box',
        gap: 20,
        position: 'relative',
      }}
    >
      {
        icon && (
          <TickerIcon
            icon={icon}
            size={tickerStyle.fontSize}
          />
        )
      }

      <Typography
        style={tickerStyle}
        text={content}
        codification={theme.codification === 'all' || theme.codification.includes('ticker')}
      />
    </div>
  );
}

